import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, ElementRef, Input, OnDestroy, ViewContainerRef } from '@angular/core';
import { merge, Observable, Subscription } from 'rxjs';
import { ContextMenuPanel } from '../../../Views/Work/Accounts/proprietary/controls/multi-tab-context-menu.component';

@Directive({
    selector: '[multiTabContextMenuTrigger]',
    host: {
        '(contextmenu)': '$event.preventDefault(); toggleContextMenu()'
    },
    standalone: false
})
export class MultiTabContextMenuTriggerDirective implements OnDestroy {
    private isContextMenuOpen = false;
    private overlayRef: OverlayRef;
    private contextMenuClosingActionsSub = Subscription.EMPTY;

    @Input('multiTabContextMenuTrigger') public contextMenuPanel: ContextMenuPanel;

    constructor(
        private overlay: Overlay,
        private elementRef: ElementRef<HTMLElement>,
        private viewContainerRef: ViewContainerRef
    ) { }

    toggleContextMenu() {
        this.isContextMenuOpen ? this.destroyContextMenu() : this.openContextMenu();
    }

    openContextMenu() {
        this.isContextMenuOpen = true;

        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            disposeOnNavigation: true,
            scrollStrategy: this.overlay.scrollStrategies.close(),
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(this.elementRef)
                .withViewportMargin(5)
                .withFlexibleDimensions(true)
                .withPositions([
                    {
                        originX: 'center',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: 3
                    },
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'bottom',
                        offsetY: 3
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                        offsetY: 3
                    }
                ])
                .withPush(true)
        });

        const templatePortal = new TemplatePortal(this.contextMenuPanel.templateRef, this.viewContainerRef);

        this.overlayRef.attach(templatePortal);

        this.contextMenuClosingActionsSub = this.contextMenuClosingActions().subscribe(() => this.destroyContextMenu());
    }

    private contextMenuClosingActions(): Observable<MouseEvent | void> {
        const backdropClick$ = this.overlayRef.backdropClick();
        const detachment$ = this.overlayRef.detachments();
        const contextMenuClosed = this.contextMenuPanel.closed;

        return merge(backdropClick$, detachment$, contextMenuClosed);
    }

    private destroyContextMenu() {
        if (!this.overlayRef || !this.isContextMenuOpen) {
            return;
        }

        this.contextMenuClosingActionsSub.unsubscribe();
        this.isContextMenuOpen = false;
        this.overlayRef.detach();
    }

    ngOnDestroy() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }
}
